import React, { useEffect, useRef, useState } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Card, CardBody, Col } from 'reactstrap';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { GET_FILTERS_LIST } from '../../../../../../store/actions';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface RootState {
  GetFiltersList?: {
    data?: any;
    loading?: boolean;
  };
}

const BarChart = ({ data, title, filter, setFilter }) => {
  const { filterAPiOptions } = useSelector((state: RootState) => ({
    filterAPiOptions: state.GetFiltersList.data,
  }));

  const [selectedItems, setSelectedItems] = useState([]);
  const [projectOption, setProjectOption] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);

  useEffect(() => {
    if (data) {
      setSelectedItems(data?.distributions);
    }
  }, [data]);

  useEffect(() => {
    if (filterAPiOptions) {
      const regionOptions = filterAPiOptions?.results?.[0]?.data?.map(item => ({
        value: item?.value,
        label: item?.value,
      }));

      const countryOptions = filterAPiOptions?.results?.[5]?.data?.map(item => ({
        value: item?.value,
        label: item?.value,
      }));

      setProjectOption(regionOptions);
      setCountryOptions(countryOptions);
    }
  }, [filterAPiOptions]);

  const barData = {
    labels: selectedItems?.map(item => item?.name),
    datasets: [
      {
        label: 'Values',
        data: selectedItems?.map(item => item?.value),
        backgroundColor: selectedItems?.map(item => item?.color),
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        labels: {
          generateLabels: chart => {
            return selectedItems?.map((item, index) => ({
              text: `${item?.vendorName} - ${item?.value}`,
              fillStyle: item.color,
              strokeStyle: item.color,
              index,
            }));
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || '';
            const value = context.raw || 0;
            return `${label}: ${value}`;
          },
        },
      },
      datalabels: {
        color: '#ffffff', // white text
        anchor: 'end',
        align: 'start',
        font: {
          weight: 'bold',
        },
        formatter: (value: number) => value,
      },
      scales: {
        x: {
          ticks: {
            font: {
              size: 5, // 👈 Change this value to your desired font size
            },
          },
        },
      },
    },
  };

  return (
    <Col xl={6} md={12} lg={6} className="mb-2" style={{ borderRadius: '10px' }}>
      <Card>
        <div className="d-flex justify-content-between m-4 align-items-center">
          <h4 className="card-title mb-0 flex-grow-1 fw-semibold">{title}</h4>
          <div className="d-flex gap-2 align-items-center">
            <Select
              options={projectOption}
              value={filter?.project}
              placeholder="Select Project"
              onChange={data => {
                setFilter(prev => ({
                  ...prev,
                  project: data,
                }));
              }}
              styles={{
                control: provided => ({
                  ...provided,
                  width: '150px', // Set custom width
                }),
              }}
            />

            <Select
              options={countryOptions}
              value={filter?.country}
              placeholder="Country"
              onChange={data => {
                setFilter(prev => ({
                  ...prev,
                  country: data,
                }));
              }}
              styles={{
                control: provided => ({
                  ...provided,
                  width: '150px', // Set custom width
                }),
              }}
            />
          </div>
        </div>

        <CardBody style={{ maxHeight: '420px', position: 'relative', textAlign: 'center', height: '402px' }}>
          <div style={{ height: '300px', position: 'relative' }}>
            <Bar data={barData} options={options} />
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

export default BarChart;
